/**
 * The keyboard.
 *
 * Every shortcut in §6.5, in one table, which is also what the `?` overlay
 * reads from - a shortcut list that can drift from the shortcuts is worse than
 * no list.
 *
 * Three rules throughout:
 *   - Nothing fires while the customer is typing in a field.
 *   - Nothing fires that the control with focus has already handled.
 *   - Nothing fires that the browser needs. Ctrl+A is ours only when the canvas
 *     has focus, because stealing it from an address bar is hostile.
 */

import { useEffect } from 'react';
import { useEditor } from './store';
import {
  addPage,
  addShape,
  addText,
  deleteSelected,
  duplicateSelected,
  groupSelected,
  nudgeSelected,
  reorderSelected,
  selectAll,
  toggleLock,
  ungroupSelected,
} from './actions';
import { isTyping } from '../canvas/CanvasView';
import { MAX_ZOOM, MIN_ZOOM } from '../canvas/CanvasView';

/** How far an arrow key moves something, in millimetres. */
const NUDGE = 0.5;
const NUDGE_BIG = 5;

export interface ShortcutRow {
  keys: string;
  what: string;
  group: string;
}

/** For the `?` overlay. Order is reading order, not code order. */
export const SHORTCUTS: ShortcutRow[] = [
  { group: 'Editing', keys: 'Ctrl Z', what: 'Undo' },
  { group: 'Editing', keys: 'Ctrl Y', what: 'Redo' },
  { group: 'Editing', keys: 'Ctrl D', what: 'Duplicate' },
  { group: 'Editing', keys: 'Delete', what: 'Delete' },
  { group: 'Editing', keys: 'Ctrl A', what: 'Select everything' },
  { group: 'Editing', keys: 'Esc', what: 'Deselect, or stop typing' },
  { group: 'Editing', keys: 'Enter', what: 'Type into the selected text, crop the picture, or edit the drawing\'s points' },
  { group: 'Editing', keys: 'Escape', what: 'Finish, or leave a crop as it was' },
  { group: 'Editing', keys: 'Double click', what: 'Type, with the caret where you clicked' },

  { group: 'Points', keys: 'Alt click', what: 'Switch a point between corner and curve' },
  { group: 'Points', keys: 'Alt drag', what: 'Move one handle on its own' },
  { group: 'Points', keys: 'Double click', what: 'Add a point on the outline' },
  { group: 'Points', keys: 'Delete', what: 'Remove the chosen point' },
  { group: 'Points', keys: 'Tab', what: 'Choose the next point' },
  { group: 'Points', keys: 'Arrows', what: 'Move the chosen point' },

  { group: 'Adding', keys: 'T', what: 'Add text' },
  { group: 'Adding', keys: 'S', what: 'Add a rectangle' },
  { group: 'Adding', keys: 'I', what: 'Add a picture' },
  { group: 'Adding', keys: 'Q', what: 'Add a QR code' },
  { group: 'Adding', keys: 'P', what: 'Draw a path' },
  { group: 'Adding', keys: 'N', what: 'Draw freehand' },
  { group: 'Adding', keys: 'Ctrl Shift P', what: 'Add a page' },

  { group: 'Panels', keys: 'L', what: 'Layers' },
  { group: 'Panels', keys: 'D', what: 'My designs' },

  { group: 'Arranging', keys: 'Arrows', what: 'Nudge 0.5 mm' },
  { group: 'Arranging', keys: 'Shift Arrows', what: 'Nudge 5 mm' },
  { group: 'Arranging', keys: 'Ctrl G', what: 'Group' },
  { group: 'Arranging', keys: 'Ctrl Shift G', what: 'Ungroup' },
  { group: 'Arranging', keys: '] [', what: 'Forwards, backwards' },
  { group: 'Arranging', keys: 'Ctrl ] [', what: 'To front, to back' },
  { group: 'Arranging', keys: 'Ctrl L', what: 'Lock or unlock' },

  { group: 'Looking', keys: 'Ctrl 0', what: 'Fit the page' },
  { group: 'Looking', keys: 'Ctrl 1', what: 'Actual size' },
  { group: 'Looking', keys: 'Space drag', what: 'Pan' },
  { group: 'Looking', keys: 'Ctrl wheel', what: 'Zoom' },
  { group: 'Looking', keys: 'R', what: 'Rulers' },
  { group: 'Looking', keys: 'G', what: 'Grid' },
  { group: 'Looking', keys: 'W', what: 'Preview' },
  { group: 'Looking', keys: '?', what: 'This list' },
];

interface Handlers {
  onFit: () => void;
  onSave: () => void;
  onHelp: () => void;
  /** Opens a side panel, or closes it if it is the one already open. */
  onPanel: (which: 'layers' | 'templates') => void;
  /** Opens the file picker. It lives in the app, not here. */
  onImage: () => void;
  /**
   * Opens Elements, where a QR code is asked what it should hold.
   *
   * The key used to add one outright, with the shop's address in it as a
   * placeholder - which is how a card gets printed pointing at the wrong place.
   */
  onCode: () => void;
  /** Picks up the pen or the pencil. The tool is the app's to hold, like the panels. */
  onDraw: (which: 'pen' | 'pencil') => void;
}

export function useShortcuts({ onFit, onSave, onHelp, onPanel, onImage, onCode, onDraw }: Handlers) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (isTyping(event.target)) return;

      /*
       * A key the focused control already handled is not ours as well. A
       * gradient stop takes Delete and the arrows, the spectrum takes the
       * arrows - and those keys used to carry on up to here, so removing a
       * stop also deleted the shape, and nudging one nudged the shape too.
       */
      if (event.defaultPrevented) return;

      const store = useEditor.getState();
      const mod = event.ctrlKey || event.metaKey;
      const key = event.key;

      // Nudging first: it is the most used and the most latency-sensitive.
      if (!mod && key.startsWith('Arrow')) {
        const step = event.shiftKey ? NUDGE_BIG : NUDGE;
        const dx = key === 'ArrowLeft' ? -step : key === 'ArrowRight' ? step : 0;
        const dy = key === 'ArrowUp' ? -step : key === 'ArrowDown' ? step : 0;

        if (dx || dy) {
          event.preventDefault();
          nudgeSelected(dx, dy);
        }

        return;
      }

      if (mod) {
        switch (key.toLowerCase()) {
          case 'z':
            event.preventDefault();
            event.shiftKey ? store.redo() : store.undo();
            return;
          case 'y':
            event.preventDefault();
            store.redo();
            return;
          case 'd':
            event.preventDefault();
            duplicateSelected();
            return;
          case 'a':
            event.preventDefault();
            selectAll();
            return;
          case 'g':
            event.preventDefault();
            event.shiftKey ? ungroupSelected() : groupSelected();
            return;
          case 'l':
            event.preventDefault();
            toggleLock();
            return;
          case 's':
            event.preventDefault();
            onSave();
            return;
          case 'p':
            if (event.shiftKey) {
              event.preventDefault();
              addPage();
            }
            return;
          case ']':
            event.preventDefault();
            reorderSelected('front');
            return;
          case '[':
            event.preventDefault();
            reorderSelected('back');
            return;
          case '0':
            event.preventDefault();
            onFit();
            return;
          case '1':
            event.preventDefault();
            store.setView({ zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, 1)) });
            return;
          default:
            return;
        }
      }

      switch (key) {
        case 'Enter': {
          const only = store.selection.length === 1 ? store.selection[0] : null;
          const page = store.doc?.pages[store.pageIndex];
          const layer = only && page ? page.layers.find((l) => l.id === only) : null;

          // Cropping takes Enter first: it is the mode that is on screen, and
          // Enter is how every other mode here is finished.
          if (store.cropping) {
            event.preventDefault();
            store.endCrop();

            return;
          }

          if (store.shaping) {
            event.preventDefault();
            store.endShaping();

            return;
          }

          if (layer && layer.type === 'shape' && layer.shape === 'free' && !layer.locked) {
            event.preventDefault();
            store.beginShaping(layer.id);

            return;
          }

          if (layer && layer.type === 'image' && layer.asset && !layer.locked) {
            event.preventDefault();
            store.beginCrop(layer.id, layer.crop);

            return;
          }

          if (layer && layer.type === 'text' && !layer.locked) {
            event.preventDefault();
            // Whole text selected, so typing replaces it - which is what a
            // customer who just added a box saying "Your text here" wants.
            store.beginEditing(layer.id, { start: 0, end: layer.text.length });
          }

          return;
        }
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          deleteSelected();
          return;
        case 'Escape':
          // Out of the crop first, and back to the picture as it was. Only a
          // second Escape gives up the selection - one key should undo one
          // thing.
          if (store.cropping) {
            store.endCrop(false);

            return;
          }

          if (store.shaping) {
            store.endShaping();

            return;
          }

          store.select([]);
          return;
        case ']':
          reorderSelected('up');
          return;
        case '[':
          reorderSelected('down');
          return;
        case '?':
          onHelp();
          return;
        case 't':
        case 'T':
          addText();
          return;
        case 's':
        case 'S':
          addShape('rect');
          return;
        case 'r':
        case 'R':
          store.setView({ showRulers: !store.view.showRulers });
          return;
        case 'g':
        case 'G':
          store.setView({ showGrid: !store.view.showGrid });
          return;
        case ';':
          store.setView({ showGuides: !store.view.showGuides });
          return;
        case 'q':
        case 'Q':
          onCode();
          return;
        case 'i':
        case 'I':
          onImage();
          return;
        /*
         * P is the pen in every vector tool there is, and the rail already said
         * so - while the key itself toggled preview. Preview moved to W, which is
         * what InDesign uses for the same thing: the view with the guides gone.
         */
        case 'p':
        case 'P':
          onDraw('pen');
          return;
        case 'n':
        case 'N':
          onDraw('pencil');
          return;
        case 'w':
        case 'W':
          store.setView({ preview: !store.view.preview });
          return;
        case 'l':
        case 'L':
          onPanel('layers');
          return;
        case 'd':
        case 'D':
          onPanel('templates');
          return;
        default:
      }
    };

    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [onFit, onSave, onHelp, onPanel, onImage, onCode, onDraw]);
}
